import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, TextInput, Pressable } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import { loadForecastDuplicates, deleteUserData } from '../db/company';
import { selectedJobAtom, selectedVersionAtom } from '../store/company';
import moment from 'moment';

// Tabs this cleanup tool covers. Matches the natural-key dup query on the
// server (readForecastDuplicates.sql).
const THIRD_PARTY_TABS = ['Materials', 'Rentals', 'Subcontractors', 'Other'];

// Server (Azure SQL) stores DSS_* datetimes as UTC. Show them in the viewer's
// local zone, same as the Change History page.
// DSS_* datetimes are written by GETDATE() on the SQL server (which runs in US
// Central), so the stored value is already Central wall-clock, not UTC. Read the
// components as-is with moment.utc and do NOT call .local() — that would subtract
// the Central offset again and show timestamps ~5h early.
const fmtDate = (d) => d ? moment.utc(d).format('YYYY-MM-DD HH:mm') + ' CT' : '';

const fmtMoney = (v) => {
    if (v === null || v === undefined || v === '') return '';
    const n = Number(v);
    if (isNaN(n)) return String(v);
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Monochrome trash glyph (no color emoji, per house style).
const TrashIcon = ({ color = '#dc2626', size = 15 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6M14 11v6" />
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
);

const DataCleanup = () => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);

    // Default to the current version (dups live within a version); job blank so
    // the whole version is swept. Both clearable.
    const [filterJob, setFilterJob] = useState('');
    const [filterVersion, setFilterVersion] = useState(selectedVersion || '');
    // 2026 jobs: BU_ID with "26" in positions 4-5 (LIKE '___26%').
    const [only2026, setOnly2026] = useState(false);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [deletingGuid, setDeletingGuid] = useState(null);
    const [status, setStatus] = useState(null);

    const fetchDups = useCallback(async () => {
        setLoading(true);
        setError(null);
        setStatus(null);
        try {
            const res = await loadForecastDuplicates({
                buId: filterJob || null,
                buIdLike: only2026 ? '___26%' : null,
                versionGuid: filterVersion || null,
            });
            if (res?.error) throw new Error(res.error);
            setRows(res?.data || []);
        } catch (e) {
            setError(String(e?.message || e));
        } finally {
            setLoading(false);
        }
    }, [filterJob, filterVersion, only2026]);

    // Pick up the current version when it changes (e.g. user switches jobs).
    useEffect(() => { setFilterVersion(selectedVersion || ''); }, [selectedVersion]);

    useEffect(() => { fetchDups(); }, [fetchDups]);

    // Group the flat row list by the natural key. Only keep groups that still
    // have 2+ members (a group drops off the list once cleaned down to one).
    const groups = useMemo(() => {
        const map = new Map();
        rows.forEach((r) => {
            const key = [r.BU_ID, r.Version_GUID, r.Forecast_Tab, r.Forecast_Row_ID, r.Forecast_Field_Name].join(' | ');
            if (!map.has(key)) {
                map.set(key, {
                    key,
                    BU_ID: r.BU_ID,
                    Version_GUID: r.Version_GUID,
                    Forecast_Tab: r.Forecast_Tab,
                    Forecast_Row_ID: r.Forecast_Row_ID,
                    Forecast_Field_Name: r.Forecast_Field_Name,
                    members: [],
                });
            }
            map.get(key).members.push(r);
        });
        // Within each group, mark the most-recently-touched row as "newest" to
        // hint which copy to keep (the user still decides).
        const out = [];
        map.forEach((g) => {
            if (g.members.length < 2) return;
            let newestGuid = null;
            let newestTs = -Infinity;
            g.members.forEach((m) => {
                const ts = new Date(m.DSS_Updated_Datetime || m.DSS_Created_Datetime || 0).getTime();
                if (ts >= newestTs) { newestTs = ts; newestGuid = m.Forecast_User_Data_GUID; }
            });
            g.newestGuid = newestGuid;
            out.push(g);
        });
        return out;
    }, [rows]);

    const totalExtra = useMemo(
        () => groups.reduce((acc, g) => acc + (g.members.length - 1), 0),
        [groups]
    );

    const handleDelete = async (member) => {
        const ok = window.confirm(
            `Delete this copy?\n\n` +
            `Job ${member.BU_ID} · ${member.Forecast_Tab} · ${member.Forecast_Field_Name}\n` +
            `Row: ${member.Forecast_Row_ID}\n` +
            `GUID: ${member.Forecast_User_Data_GUID}\n` +
            `Value: ${fmtMoney(member.Value_Amount ?? member.Forecast_Value)}\n\n` +
            `This permanently removes only this one row (logged to Change History).`
        );
        if (!ok) return;
        setDeletingGuid(member.Forecast_User_Data_GUID);
        setStatus(null);
        setError(null);
        try {
            const res = await deleteUserData({
                id: member.BU_ID,
                forecastGuid: member.Version_GUID,
                rowId: String(member.Forecast_User_Data_GUID),
                tab: member.Forecast_Tab,
            });
            if (res?.error) throw new Error(res.error);
            // Optimistically drop just this GUID from local state.
            setRows((prev) => prev.filter((r) => r.Forecast_User_Data_GUID !== member.Forecast_User_Data_GUID));
            setStatus(`Deleted GUID ${member.Forecast_User_Data_GUID}.`);
        } catch (e) {
            setError(`Delete failed for GUID ${member.Forecast_User_Data_GUID}: ${String(e?.message || e)}`);
        } finally {
            setDeletingGuid(null);
        }
    };

    const th = { fontSize: 10, color: '#f1f5f9', fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, paddingRight: 8 };
    const td = { fontSize: 11, color: '#1e293b', paddingRight: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

    const COLS = [
        { label: 'Keep?', w: 70 },
        { label: 'GUID', w: 90 },
        { label: 'Value', w: 100 },
        { label: 'Cost Type', w: 80 },
        { label: 'Cost Code', w: 90 },
        { label: 'Supplier', w: 150 },
        { label: 'PO', w: 90 },
        { label: 'Description', w: 180 },
        { label: 'Created', w: 130 },
        { label: 'Updated', w: 130 },
        { label: 'By', w: 120 },
        { label: '', w: 50 },
    ];

    return (
        <View style={{ padding: 20, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4, gap: 16 }}>
                <Text style={{ fontSize: 20, fontWeight: '700', color: '#0f172a' }}>Data Cleanup — Third-Party Duplicates</Text>
            </View>
            <Text style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                Rows that share the same Job · Version · Tab · Row · Field. Review each group and delete the extra copies.
                Deleting removes only the chosen Forecast_User_Data_GUID and is logged to Change History.
            </Text>

            {/* Filter bar */}
            <View style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
                    <View>
                        <Text style={{ fontSize: 10, color: '#64748b', fontWeight: '600', marginBottom: 2 }}>Job (BU_ID)</Text>
                        <TextInput
                            value={filterJob}
                            onChangeText={setFilterJob}
                            placeholder="all jobs"
                            style={{ width: 150, height: 26, fontSize: 12, padding: '2px 6px', border: '1px solid #cbd5e1', borderRadius: 4 }}
                        />
                    </View>
                    <View>
                        <Text style={{ fontSize: 10, color: '#64748b', fontWeight: '600', marginBottom: 2 }}>Version GUID</Text>
                        <TextInput
                            value={filterVersion}
                            onChangeText={setFilterVersion}
                            placeholder="all versions"
                            style={{ width: 160, height: 26, fontSize: 12, padding: '2px 6px', border: '1px solid #cbd5e1', borderRadius: 4 }}
                        />
                    </View>
                    <Pressable
                        onPress={() => setOnly2026((v) => !v)}
                        style={{ flexDirection: 'row', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 4, cursor: 'pointer', border: '1px solid #cbd5e1', backgroundColor: only2026 ? '#1e40af' : '#ffffff', height: 30 }}
                    >
                        <Text style={{ fontSize: 12, fontWeight: '700', color: only2026 ? '#ffffff' : '#334155' }}>{only2026 ? '☑' : '☐'}</Text>
                        <Text style={{ fontSize: 12, fontWeight: '600', color: only2026 ? '#ffffff' : '#334155' }}>2026 jobs only</Text>
                    </Pressable>
                    <Pressable
                        onPress={() => fetchDups()}
                        style={{ backgroundColor: '#1e40af', padding: '6px 14px', borderRadius: 4, cursor: loading ? 'wait' : 'pointer' }}
                    >
                        <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>{loading ? 'Loading…' : 'Refresh'}</Text>
                    </Pressable>
                    <Pressable
                        onPress={() => { setFilterJob(''); setFilterVersion(''); setOnly2026(false); }}
                        style={{ backgroundColor: '#e2e8f0', padding: '6px 14px', borderRadius: 4, cursor: 'pointer' }}
                    >
                        <Text style={{ color: '#334155', fontSize: 12, fontWeight: '600' }}>Clear filters</Text>
                    </Pressable>
                    <Pressable
                        onPress={() => { setFilterJob(selectedJob || ''); setFilterVersion(selectedVersion || ''); }}
                        style={{ backgroundColor: '#e2e8f0', padding: '6px 14px', borderRadius: 4, cursor: 'pointer' }}
                    >
                        <Text style={{ color: '#334155', fontSize: 12, fontWeight: '600' }}>Scope to current job/version</Text>
                    </Pressable>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16, marginTop: 10 }}>
                    <Text style={{ fontSize: 12, color: '#0f172a', fontWeight: '600' }}>
                        {groups.length.toLocaleString()} duplicate group{groups.length === 1 ? '' : 's'} — {totalExtra.toLocaleString()} extra row{totalExtra === 1 ? '' : 's'} to clean
                    </Text>
                    {status ? <Text style={{ fontSize: 12, color: '#059669', fontWeight: '600' }}>{status}</Text> : null}
                    {error ? <Text style={{ fontSize: 12, color: '#dc2626' }}>{error}</Text> : null}
                </View>
            </View>

            {/* Empty state */}
            {!loading && groups.length === 0 ? (
                <View style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 28 }}>
                    <Text style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>
                        No duplicate third-party rows for these filters — clean.
                    </Text>
                </View>
            ) : null}

            {/* Groups */}
            {groups.map((g) => (
                <View key={g.key} style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 14, overflow: 'hidden' }}>
                    {/* Group header */}
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: '8px 12px', backgroundColor: '#fef2f2', borderBottom: '1px solid #fee2e2', flexWrap: 'wrap' }}>
                        <View style={{ backgroundColor: '#dc2626', borderRadius: 4, padding: '2px 8px' }}>
                            <Text style={{ color: '#fff', fontSize: 11, fontWeight: '700' }}>{g.members.length} copies</Text>
                        </View>
                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#0f172a' }}>{g.BU_ID}</Text>
                        <Text style={{ fontSize: 12, color: '#475569' }}>·</Text>
                        <Text style={{ fontSize: 12, fontWeight: '600', color: '#1e293b' }}>{g.Forecast_Tab}</Text>
                        <Text style={{ fontSize: 12, color: '#475569' }}>·</Text>
                        <Text style={{ fontSize: 12, color: '#1e293b' }}>Field: <Text style={{ fontWeight: '600' }}>{g.Forecast_Field_Name}</Text></Text>
                        <Text style={{ fontSize: 12, color: '#475569' }}>·</Text>
                        <Text style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>Row: {g.Forecast_Row_ID}</Text>
                        <Text style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>v{g.Version_GUID}</Text>
                    </View>

                    {/* Member header row */}
                    <View style={{ flexDirection: 'row', backgroundColor: '#1e293b', padding: '6px 10px', alignItems: 'center' }}>
                        {COLS.map((c, i) => (
                            <Text key={i} style={{ ...th, width: c.w }}>{c.label}</Text>
                        ))}
                    </View>

                    {/* Member rows */}
                    {g.members.map((m, idx) => {
                        const isNewest = m.Forecast_User_Data_GUID === g.newestGuid;
                        const isDeleting = deletingGuid === m.Forecast_User_Data_GUID;
                        return (
                            <View
                                key={m.Forecast_User_Data_GUID}
                                style={{
                                    flexDirection: 'row', padding: '5px 10px', alignItems: 'center',
                                    backgroundColor: isDeleting ? '#fff7ed' : (idx % 2 === 0 ? '#ffffff' : '#f8fafc'),
                                    borderBottom: '1px solid #f1f5f9', opacity: isDeleting ? 0.5 : 1,
                                }}
                            >
                                <View style={{ width: 70 }}>
                                    {isNewest ? (
                                        <View style={{ backgroundColor: '#dcfce7', borderRadius: 3, padding: '1px 6px', alignSelf: 'flex-start' }}>
                                            <Text style={{ fontSize: 9, fontWeight: '700', color: '#166534' }}>NEWEST</Text>
                                        </View>
                                    ) : <Text style={{ fontSize: 10, color: '#cbd5e1' }}>—</Text>}
                                </View>
                                <Text style={{ ...td, width: 90, fontFamily: 'monospace', color: '#475569' }}>{m.Forecast_User_Data_GUID}</Text>
                                <Text style={{ ...td, width: 100, fontWeight: '600', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(m.Value_Amount ?? m.Forecast_Value)}</Text>
                                <Text style={{ ...td, width: 80 }}>{m.Cost_Type || ''}</Text>
                                <Text style={{ ...td, width: 90 }}>{m.Cost_Code || ''}</Text>
                                <Text style={{ ...td, width: 150 }} title={m.Supplier_Name || ''}>{m.Supplier_Name || ''}</Text>
                                <Text style={{ ...td, width: 90 }}>{m.PO_Number || ''}</Text>
                                <Text style={{ ...td, width: 180 }} title={m.Description || m.Notes || ''}>{m.Description || m.Notes || ''}</Text>
                                <Text style={{ ...td, width: 130, fontSize: 10, color: '#64748b', fontFamily: 'monospace' }}>{fmtDate(m.DSS_Created_Datetime)}</Text>
                                <Text style={{ ...td, width: 130, fontSize: 10, color: '#64748b', fontFamily: 'monospace' }}>{fmtDate(m.DSS_Updated_Datetime)}</Text>
                                <Text style={{ ...td, width: 120, fontSize: 10 }} title={m.DSS_Updated_By || m.DSS_Created_By || ''}>{m.DSS_Updated_By || m.DSS_Created_By || ''}</Text>
                                <View style={{ width: 50, alignItems: 'center' }}>
                                    <Pressable
                                        onPress={() => !isDeleting && handleDelete(m)}
                                        style={{ padding: 5, borderRadius: 4, cursor: isDeleting ? 'wait' : 'pointer', backgroundColor: '#fee2e2' }}
                                        title="Delete this copy"
                                    >
                                        <TrashIcon />
                                    </Pressable>
                                </View>
                            </View>
                        );
                    })}
                </View>
            ))}
        </View>
    );
};

export default DataCleanup;
